import { FC, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Organization {
  id: string;
  name: string;
}

interface BatteryType {
  id: string;
  name: string;
  capacity_kwh: number;
  power_kw: number;
}

interface CreateReservationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateReservationModal: FC<CreateReservationModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [batteryTypes, setBatteryTypes] = useState<BatteryType[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    try {
      const [orgsResponse, typesResponse] = await Promise.all([
        supabase
          .from('organizations')
          .select('id, name')
          .eq('classification', 'klant')
          .order('name'),
        supabase
          .from('battery_types')
          .select('*')
          .order('name')
      ]);

      if (orgsResponse.error) throw orgsResponse.error;
      if (typesResponse.error) throw typesResponse.error;

      setOrganizations(orgsResponse.data || []);
      setBatteryTypes(typesResponse.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Fout bij ophalen gegevens',
        description: 'Kon klanten en batterij types niet laden',
        variant: 'destructive'
      });
    }
  };

  const handleCreate = async () => {
    if (!selectedOrgId || !selectedTypeId || !quantity || parseInt(quantity) < 1) {
      toast({
        title: 'Vul alle verplichte velden in',
        description: 'Selecteer een klant, type en aantal',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('reservations')
        .insert({
          org_id: selectedOrgId,
          battery_type_id: selectedTypeId,
          quantity: parseInt(quantity),
          notes,
          status: 'pending'
        });

      if (error) throw error;

      toast({
        title: 'Reservering aangemaakt',
        description: 'De reservering is succesvol aangemaakt'
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast({
        title: 'Fout bij aanmaken reservering',
        description: 'Kon reservering niet aanmaken',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedOrgId('');
    setSelectedTypeId('');
    setQuantity('1');
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Maak reservering</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Maak een nieuwe batterij reservering aan
            </p>
          </div>

          <div className="space-y-2">
            <Label>Klant *</Label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer klant" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Batterij type *</Label>
            <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer type" />
              </SelectTrigger>
              <SelectContent>
                {batteryTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name} ({type.power_kw}kW / {type.capacity_kwh}kWh)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Aantal *</Label>
            <Input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Aantal batterijen"
            />
          </div>

          <div className="space-y-2">
            <Label>Notities</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Eventuele notities..."
              rows={3}
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Annuleren
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? 'Aanmaken...' : 'Aanmaken'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
